import "./src/styles/global.css";
import type { GatsbyBrowser } from "gatsby";
import React from "react";
import { Toaster } from "sonner";
import { ToastProvider } from "./src/components/ui/new-toast";
import { storeTrackingParamsInCookies, storeRewardfulAffiliateToCookie } from "./src/lib/cookieUtils";
import { ThemeProvider } from "./src/contexts/ThemeContext";

export const onClientEntry: GatsbyBrowser["onClientEntry"] = () => {
  // Inject environment variables into window object
  // Gatsby replaces process.env at build time, but we need them in browser
  (window as any).__ENV__ = {
    SUPABASE_URL: process.env.GATSBY_SUPABASE_URL,
    SUPABASE_ANON_KEY: process.env.GATSBY_SUPABASE_ANON_KEY,
    API_BASE_URL: process.env.GATSBY_API_BASE_URL,
    GITHUB_CLIENT_ID: process.env.GATSBY_GITHUB_CLIENT_ID,
    SITE_URL: process.env.GATSBY_SITE_URL,
    POSTHOG_KEY: process.env.GATSBY_POSTHOG_KEY,
    POSTHOG_HOST: process.env.GATSBY_POSTHOG_HOST,
    REWARDFUL_API_KEY: process.env.GATSBY_REWARDFUL_API_KEY,
    APP_VERSION: process.env.GATSBY_APP_VERSION,
  };

  // Log app version for debugging
  // console.log('[App] Version:', process.env.GATSBY_APP_VERSION || 'unknown');

  // Store URL tracking parameters (UTM params, etc.) in cookies
  const searchParams = new URLSearchParams(window.location.search);
  storeTrackingParamsInCookies(searchParams);

  // IntersectionObserver polyfill for older browsers
  if (typeof window.IntersectionObserver === "undefined") {
    import("intersection-observer");
  }
};

/**
 * Check if the current user is an affiliate user
 * Returns true if user came from an affiliate link, false otherwise
 */
const isAffiliateUser = (): boolean => {
  if (typeof window === 'undefined') return false;

  /**
   * Helper function to get URL parameter
   */
  const getUrlParameter = (name: string): string | null => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(name);
  };

  /**
   * Helper function to get cookie value
   */
  const getCookie = (name: string): string | null => {
    const nameEQ = name + "=";
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  };

  /**
   * Helper function to check localStorage
   */
  const getLocalStorage = (key: string): string | null => {
    try {
      return localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  };

  /**
   * Helper function to check sessionStorage
   */
  const getSessionStorage = (key: string): string | null => {
    try {
      return sessionStorage.getItem(key);
    } catch (e) {
      return null;
    }
  };

  // 1. Check for Rewardful affiliate indicators
  const rewardfulParams = ['via', 'referral', 'ref', 'affiliate'];
  for (const param of rewardfulParams) {
    if (getUrlParameter(param)) {
      // console.log('[Affiliate Check] ✅ Rewardful param found:', param);
      return true;
    }
  }

  // Check Rewardful cookie
  if (getCookie('rewardful.referral')) {
    // console.log('[Affiliate Check] ✅ Rewardful cookie found');
    return true;
  }

  // 3. Check for other common affiliate parameters
  const otherAffiliateParams = ['aff', 'affid', 'affiliate_id', 'ref_id', 'partner'];
  for (const param of otherAffiliateParams) {
    if (getUrlParameter(param)) {
      // console.log('[Affiliate Check] ✅ Affiliate parameter found:', param);
      return true;
    }
  }

  // console.log('[Affiliate Check] ⛔ No affiliate indicators found - organic user');
  return false;
};

/**
 * Initialize PostHog and Rewardful after page is ready
 * This runs after all critical rendering is complete (post-LCP/FCP)
 *
 * onRouteUpdate fires after:
 * - Initial page load
 * - Route changes
 * - Page is fully interactive
 */
let postHogInitialized = false;
let rewardfulInitialized = false;

export const onRouteUpdate: GatsbyBrowser["onRouteUpdate"] = () => {
  // Only initialize PostHog once to prevent double navigation
  if (postHogInitialized) return;
  postHogInitialized = true;

  // Initialize PostHog during browser idle time (zero LCP/FCP impact)
  import("./src/lib/postHogClient").then(({ initPostHogWhenIdle }) => {
    initPostHogWhenIdle();
  });

  // Check if this is an affiliate user
  const isAffiliate = isAffiliateUser();

  // Only initialize Rewardful for affiliate users
  if (!rewardfulInitialized && isAffiliate) {
    rewardfulInitialized = true;
    console.log('[Rewardful] 🎯 Affiliate user detected - loading Rewardful');

    const initRewardful = () => {
      // Initialize Rewardful queue function first
      (function(w: any, r: string) {
        w._rwq = r;
        w[r] = w[r] || function() {
          (w[r].q = w[r].q || []).push(arguments);
        };
      })(window, 'rewardful');

      // Load the Rewardful script
      const script = document.createElement('script');
      script.src = 'https://r.wdfl.co/rw.js';
      script.async = true;
      script.setAttribute('data-rewardful', (window as any).__ENV__.REWARDFUL_API_KEY || '981168');
      document.head.appendChild(script);

      // Store Rewardful affiliate data to cookies when available
      const tryStoreRewardful = () => {
        const stored = storeRewardfulAffiliateToCookie();
        if (stored) {
          console.log('[Rewardful] Successfully stored affiliate to cookie');
        }
      };

      // Try immediately in case Rewardful is already loaded
      setTimeout(tryStoreRewardful, 100);

      // Also set up a listener for when Rewardful becomes ready
      if (typeof window !== 'undefined' && (window as any).rewardful) {
        (window as any).rewardful('ready', () => {
          console.log('[Rewardful] Script loaded and ready');
          tryStoreRewardful();
        });
      }

      // Fallback: Check periodically for a short time if Rewardful loads
      const checkInterval = setInterval(() => {
        if ((window as any).Rewardful?.affiliate || (window as any).Rewardful?.referral) {
          tryStoreRewardful();
          clearInterval(checkInterval);
        }
      }, 500);

      // Clean up interval after 5 seconds
      setTimeout(() => {
        clearInterval(checkInterval);
      }, 5000);
    };

    if (typeof (window as any).requestIdleCallback === 'function') {
      (window as any).requestIdleCallback(initRewardful, { timeout: 2000 });
    } else {
      setTimeout(initRewardful, 100);
    }
  } else if (!rewardfulInitialized) {
    console.log('[Rewardful] ⛔ Organic user - skipping Rewardful load');
  }
}

export const wrapPageElement: GatsbyBrowser["wrapPageElement"] = ({ element }) => {
  return (
    <ThemeProvider>
    <ToastProvider>
      {element}
      <Toaster position="bottom-left" closeButton />
    </ToastProvider>
    </ThemeProvider>
  );
};